import { EditableFieldProps } from '../core-field/editable-field-json-props'
import { JustificationReason } from '../field-ui/action-justification-panel/action-justification-panel-props'
import { BlindMatchValue, StatusTooltipOverrides } from './types'

/**
 * Blind-match field: the user types a value that is compared against a
 * hidden source. The source may be supplied inline (`source`), resolved
 * from the current entity context via `sourcePath` + `entity`, or fetched
 * from a different entity edge. Match state surfaces as a status badge;
 * save can be gated by binding to a `blindMatchWithOverride` schema path
 * with `properties.verified.const: true`.
 */
export const UI2_BLIND_MATCH_FIELD_DEFAULT_PROPS: Partial<UI2BlindMatchFieldProps> = {
  showUnavailableOnEmptySource: true,
  placeholder: 'Enter value to verify',
}

export interface UI2BlindMatchFieldProps extends EditableFieldProps<BlindMatchValue> {
  // ─── Source ─────────────────────────────────────────────────────────────
  /** Inline source value to compare against. Takes precedence over `sourcePath` and `entity`. */
  source?: string
  /**
   * Path on `entity` to read the hidden source value from.
   * Requires `entity` to be provided.
   */
  sourcePath?: string
  /** Entity object from which `sourcePath` is read. Typically injected by the renderer. */
  entity?: any
  /**
   * When `true` (default), the badge shows `Unavailable` if the source
   * is absent or empty. When `false`, the field behaves as a plain text
   * input with no match check until a source becomes available.
   */
  showUnavailableOnEmptySource?: boolean
  /**
   * Show a reveal toggle so the user can inspect the hidden source value
   * while in a mismatch or override state.
   */
  allowRevealSource?: boolean

  // ─── Override ───────────────────────────────────────────────────────────
  /**
   * Show the override panel when the typed value does not match the source.
   * The user must provide a reason before the value is considered verified
   * (`verified: true` with `overridden: true`).
   */
  allowOverride?: boolean
  /**
   * Canned override reasons shown in the justification picker.
   * Strings are treated as `{ label: s, value: s }`.
   * Omit to show a freeform-only textarea.
   */
  overrideReasons?: Array<string | JustificationReason>
  /**
   * Error text shown when the value does not match the source and
   * `allowOverride` is false.
   * @default 'The entered value must match the source value.'
   */
  matchFailedErrorText?: string
  /**
   * Label for the override toggle switch.
   * @default 'Override source value?'
   */
  overrideToggleLabel?: string

  // ─── Status badge ────────────────────────────────────────────────────────
  /**
   * Per-status tooltip text overrides for the status badge popover.
   * Any key omitted uses the default copy from the badge component.
   */
  statusTooltips?: StatusTooltipOverrides
}
